import { Injectable } from '@angular/core';
import { defaultPageSize } from '../constants';
import {
	map,
	catchError,
	shareReplay,
	distinctUntilChanged,
} from 'rxjs/operators';
import { mergeWith, isObject, isEmpty, isEqual, get, merge } from 'lodash';
import * as moment from 'moment';
import { Observable, combineLatest, BehaviorSubject, of } from 'rxjs';
import { UserClient } from '../api';
import { UserPreferences } from '../models/UserPreferences';
import { USER_PREFERENCES_KEY } from 'src/admin-app/shared/+state/local-storage/local-storage-keys';
import { LocalStorageService } from '../local-storage.service';

@Injectable({
	providedIn: 'root',
})
export class UserPreferencesService {
	protected UserPreferenceSubjects: {
		[key: string]: BehaviorSubject<any>;
	} = {};

	defaultPreferences: UserPreferences = {
		listPageSize: {
			id: -1,
			value: defaultPageSize.toString(),
			modified: moment.utc().toJSON(),
		},
	};

	readonly userPreferences$: Observable<
		UserPreferences
	> = this.localStorageService
		.select(USER_PREFERENCES_KEY)
		.pipe(
			map((currentPrefs) =>
				isEmpty(currentPrefs) ? this.defaultPreferences : currentPrefs
			)
		);

	readonly pageSize$ = this.userPreferences$.pipe(
		map((uP) =>
			uP.listPageSize && uP.listPageSize.value
				? +uP.listPageSize.value
				: +defaultPageSize
		),
		distinctUntilChanged()
	);

	readonly filterTeamId$ = this.userPreferences$.pipe(
		map((userPrefs) =>
			userPrefs.filterTeamId && userPrefs.filterTeamId.value
				? +userPrefs.filterTeamId.value
				: null
		)
	);

	constructor(
		private userPreferencesClient: UserClient,
		private localStorageService: LocalStorageService
	) {}

	userPreferences() {
		return combineLatest([
			this.localStorageService.select(USER_PREFERENCES_KEY) as Observable<
				UserPreferences
			>,
			this.userPreferencesClient.userPreferences().pipe(
				catchError(() => {
					// never mind then...
					return of(null);
				})
			),
		]).pipe(
			map(([prefsOne, prefsTwo]) =>
				this.mergePreferences(prefsOne, prefsTwo)
			),
			map((e) => (isEmpty(e) ? this.defaultPreferences : e)),
			shareReplay(1)
		);
	}

	setListPageSizeLocal(pageSize: number | string) {
		const currentPrefs = this.localStorageService.getItem(
			USER_PREFERENCES_KEY
		).value;
		if (+currentPrefs.listPageSize?.value !== +pageSize) {
			const updatedPrefs = merge(
				currentPrefs,
				pageSize
					? {
						listPageSize: {
							id: get(currentPrefs, 'listPageSize.id', -1),
							value: pageSize.toString(),
							modified: moment.utc(),
						},
					}
					: this.defaultPreferences
			);

			this.localStorageService.set(USER_PREFERENCES_KEY, updatedPrefs);
			return true;
		}
		return false;
	}

	setListPageSizeApi(pageSize: number | string) {
		const currentPrefs = this.localStorageService.getItem(
			USER_PREFERENCES_KEY
		).value;
		const updatedPrefs = merge(
			currentPrefs,
			pageSize
				? {
					listPageSize: {
						id: get(currentPrefs, 'listPageSize.id', -1),
						value: pageSize.toString(),
						modified: moment.utc(),
					},
				}
				: this.defaultPreferences
		);

		this.userPreferencesClient
			.addOrUpdateUserPreferences(updatedPrefs)
			.subscribe();
	}

	listPageSize(): Observable<number> {
		return this.userPreferences().pipe(
			distinctUntilChanged((p, c) =>
				isEqual(
					+get(p, 'listPageSize.value'),
					+get(c, 'listPageSize.value')
				)
			),
			map((uP) =>
				uP.listPageSize && uP.listPageSize.value
					? +uP.listPageSize.value
					: +defaultPageSize
			)
		);
	}

	setFilterTeamIdLocal(filterTeamId: number | null) {
		const currentPrefs = this.localStorageService.getItem(
			USER_PREFERENCES_KEY
		).value;
		if (+(currentPrefs.filterTeamId?.value ?? 0) !== filterTeamId) {
			const updatedPrefs = merge(currentPrefs, {
				filterTeamId: {
					id: get(currentPrefs, 'filterTeamId.id', -1),
					value: filterTeamId ? filterTeamId.toString() : null,
					modified: moment.utc(),
				},
			});

			this.localStorageService.set(USER_PREFERENCES_KEY, updatedPrefs);

			return true;
		}
		return false;
	}

	setFilterTeamIdApi(filterTeamId: number | null) {
		const currentPrefs = this.localStorageService.getItem(
			USER_PREFERENCES_KEY
		).value;
		const updatedPrefs = merge(currentPrefs, {
			filterTeamId: {
				id: get(currentPrefs, 'filterTeamId.id', -1),
				value: filterTeamId ? filterTeamId.toString() : null,
				modified: moment.utc(),
			},
		});

		this.userPreferencesClient
			.addOrUpdateUserPreferences(updatedPrefs)
			.subscribe();
	}

	mergePreferences(prefsOne: UserPreferences, prefsTwo: UserPreferences) {
		return mergeWith(prefsOne, prefsTwo, (settingOne, settingTwo) => {
			const modifiedOne =
				settingOne &&
				settingOne.modified &&
				moment.utc(settingOne.modified);
			const modifiedTwo =
				settingTwo &&
				settingTwo.modified &&
				moment.utc(settingTwo.modified);
			return isObject(modifiedOne) && isObject(modifiedTwo)
				? modifiedOne.isAfter(modifiedTwo)
					? settingOne
					: settingTwo
				: !modifiedOne
					? settingTwo
					: settingOne;
		});
	}
}
